// ==========================================================================
// React Query Hook for Alert Data
// ==========================================================================

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAlert } from '@/services/alert.service';

// Query keys
export const alertKeys = {
  all: ['alert'] as const,
  current: () => [...alertKeys.all, 'current'] as const,
};

/**
 * Hook to fetch current user alert
 * Returns null if no active alert
 */
export function useAlert(enabled = true) {
  return useQuery({
    queryKey: alertKeys.current(),
    queryFn: getAlert,
    enabled: enabled && !!localStorage.getItem('access_token'),
    staleTime: 5 * 60 * 1000, // Data fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes
    // Refetch when the user returns to the PWA tab or unlocks their device.
    // Without this, an alert removed admin-side stays cached for up to 5
    // minutes, blocking the pre-alert drawer/banner from reappearing.
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: (failureCount, error) => {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 401) return false;
      return failureCount < 2;
    },
  });
}

/**
 * Hook to invalidate alert cache (force refetch)
 */
export function useInvalidateAlert() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: alertKeys.all });
}

/**
 * Hook to clear alert cache
 */
export function useClearAlertCache() {
  const queryClient = useQueryClient();
  return () => queryClient.removeQueries({ queryKey: alertKeys.all });
}
